import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { formatContract } from '../utils/ebuParser';
import { calculateTravellerPercentages } from '../utils/pairsScoring';
import HandDiagram from '../components/HandDiagram';
import './GameView.css';

function GameView() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scores');
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [expandedView, setExpandedView] = useState(false);
  const [travellerViewMode, setTravellerViewMode] = useState('all'); // 'all' or 'single'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Mobile detection hook
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for switch to individual scorecard from travellers
  useEffect(() => {
    const handleSwitchToIndividual = (event) => {
      setActiveTab('individual');
      setTimeout(() => {
        const selectPairEvent = new CustomEvent('selectPair', { detail: event.detail });
        window.dispatchEvent(selectPairEvent);
      }, 100);
    };
    window.addEventListener('switchToIndividual', handleSwitchToIndividual);
    return () => window.removeEventListener('switchToIndividual', handleSwitchToIndividual);
  }, []);

  const fetchGame = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setGame(data);
      
      // Set first board as selected by default for travellers
      if (data.travellers) {
        const boards = Object.keys(data.travellers);
        if (boards.length > 0) {
          setSelectedBoard(boards[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching game:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchGame();
  }, [fetchGame]);

  if (loading) {
    return <div className="container"><p>Loading game...</p></div>;
  }

  if (!game) {
    return <div className="container"><p>Game not found.</p></div>;
  }

  return (
    <div className="container game-view">
      <div className="game-header">
        <h2>{game.title}</h2>
        <div className="game-info">
          <span>{new Date(game.game_date).toLocaleDateString('en-GB')}</span>
          <span>{game.game_type === 'teams' ? 'Teams' : 'Pairs'}</span>
          {game.director && <span>Director: {game.director}</span>}
        </div>
      </div>

      <div className="tabs">
        <button 
          className={activeTab === 'scores' ? 'active' : ''} 
          onClick={() => setActiveTab('scores')}
        >
          Summary
        </button>
        {game.game_type === 'teams' && (
          <button 
            className={activeTab === 'teamscoresheet' ? 'active' : ''} 
            onClick={() => setActiveTab('teamscoresheet')}
          >
            Team Scoresheet
          </button>
        )}
        <button 
          className={activeTab === 'travellers' ? 'active' : ''} 
          onClick={() => setActiveTab('travellers')}
        >
          Travellers
        </button>
        {game.game_type === 'pairs' && (
          <button 
            className={activeTab === 'individual' ? 'active' : ''} 
            onClick={() => setActiveTab('individual')}
          >
            Individual Scorecard
          </button>
        )}
      </div>

      {activeTab === 'scores' && (
        <ScoreSheet 
          scores={game.scores} 
          gameType={game.game_type} 
          metadata={game.metadata}
          travellers={game.travellers}
          expandedView={expandedView}
          setExpandedView={setExpandedView}
          onPlayerClick={(pairNum) => {
            if (game.game_type === 'teams') {
              // For teams, extract team number from partnership ID like "1-NS"
              const teamNum = pairNum.split('-')[0];
              setActiveTab('teamscoresheet');
              setTimeout(() => {
                const event = new CustomEvent('selectTeam', { detail: teamNum });
                window.dispatchEvent(event);
              }, 100);
            } else {
              setActiveTab('individual');
              setTimeout(() => {
                const event = new CustomEvent('selectPair', { detail: pairNum });
                window.dispatchEvent(event);
              }, 100);
            }
          }}
        />
      )}

      {activeTab === 'travellers' && (
        <Travellers 
          travellers={game.travellers} 
          selectedBoard={selectedBoard}
          setSelectedBoard={setSelectedBoard}
          handRecords={game.hand_records}
          viewMode={travellerViewMode}
          setViewMode={setTravellerViewMode}
          gameType={game.game_type}
          metadata={game.metadata}
        />
      )}

      {activeTab === 'individual' && (
        <IndividualScorecard 
          travellers={game.travellers}
          gameType={game.game_type}
          handRecords={game.hand_records}
          metadata={game.metadata}
          isMobile={isMobile}
          onPlayerClick={(pairNum) => {
            // Stay on individual tab, just change selection
            setTimeout(() => {
              const event = new CustomEvent('selectPair', { detail: pairNum });
              window.dispatchEvent(event);
            }, 100);
          }}
        />
      )}

      {activeTab === 'teamscoresheet' && game.game_type === 'teams' && (
        <TeamScoresheet 
          scores={game.scores}
          travellers={game.travellers}
          handRecords={game.hand_records}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

function ScoreSheet({ scores, gameType, metadata, travellers, expandedView, setExpandedView, onPlayerClick }) {
  // Detect IMP scoring for pairs
  const isIMPScoring = gameType === 'pairs' && metadata?.Scoring === '2';
  
  // Sort scores by position for pairs
  const sortedScores = gameType === 'pairs' 
    ? [...scores].sort((a, b) => parseInt(a.position) - parseInt(b.position))
    : scores;

  // Calculate scaled scores for IMP pairs
  const displayScores = sortedScores.map(score => {
    if (isIMPScoring && travellers) {
      // Count number of tables from first board's traveller
      const firstBoard = Object.keys(travellers)[0];
      const numTables = travellers[firstBoard]?.length || 1;
      const scaleFactor = numTables > 1 ? (numTables - 1) : 1;
      
      return {
        ...score,
        scaledScore: (parseFloat(score.score) / scaleFactor).toFixed(1)
      };
    }
    return score;
  });

  return (
    <div className="scoresheet">
      {/* Summary table always shown first */}
      <table>
        <thead>
          <tr>
            <th>Pos</th>
            {gameType === 'teams' ? (
              <>
                <th>Team</th>
                <th>Players</th>
                <th>IMPs</th>
                <th>VPs</th>
                </>
              ) : (
                <>
                  <th>Pair</th>
                  <th>Players</th>
                  <th>{isIMPScoring ? 'IMPs' : 'Score'}</th>
                  {!isIMPScoring && <th>%</th>}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {displayScores.map((score, idx) => (
              <tr key={idx}>
                <td>{score.position}</td>
                {gameType === 'teams' ? (
                  <>
                    <td>{score.team}</td>
                    <td>
                      <div className="team-partnerships">
                        <div 
                          className="clickable-players partnership-ns"
                          onClick={() => onPlayerClick && onPlayerClick(`${score.team}-NS`)}
                          title="North-South pair"
                        >
                          <strong>NS:</strong> {score.players[0]} - {score.players[1]}
                        </div>
                        <div 
                          className="clickable-players partnership-ew"
                          onClick={() => onPlayerClick && onPlayerClick(`${score.team}-EW`)}
                          title="East-West pair"
                        >
                          <strong>EW:</strong> {score.players[2]} - {score.players[3]}
                        </div>
                      </div>
                    </td>
                    <td>{score.imps > 0 ? '+' : ''}{score.imps}</td>
                    <td>{score.vps.toFixed(2)}</td>
                  </>
                ) : (
                  <>
                    <td>{score.pair}</td>
                    <td 
                      className="clickable-players"
                      onClick={() => onPlayerClick && onPlayerClick(score.pair)}
                    >
                      {score.players.join(' - ')}
                    </td>
                    <td>{isIMPScoring ? score.scaledScore : score.score}</td>
                    {!isIMPScoring && <td>{score.percent.toFixed(1)}%</td>}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      
      {/* Board-by-board toggle button and view for teams */}
      {gameType === 'teams' && (
        <>
        </>
      )}
    </div>
  );
}

function Travellers({ travellers, selectedBoard, setSelectedBoard, handRecords, viewMode, setViewMode, gameType, metadata }) {
  const boards = Object.keys(travellers).sort((a, b) => parseInt(a) - parseInt(b));
  const [switchToIndividual, setSwitchToIndividual] = React.useState(null);

  // Detect IMP scoring for pairs
  const isIMPScoring = gameType === 'pairs' && metadata?.Scoring === '2';

  // Effect to trigger tab switch when a pair is clicked
  React.useEffect(() => {
    if (switchToIndividual) {
      const event = new CustomEvent('switchToIndividual', { detail: switchToIndividual });
      window.dispatchEvent(event);
      setSwitchToIndividual(null);
    }
  }, [switchToIndividual]);
  
  // Calculate percentages for pairs (only for MP scoring)
  const travellersWithPercentages = (gameType === 'pairs' && !isIMPScoring)
    ? calculateTravellerPercentages(travellers) 
    : travellers;
  
  // Function to copy PBN to clipboard
  const copyPBN = async (boardNum, hand) => {
    const { deal, dealer, vulnerable } = hand;
    
    // Format vulnerability
    let vulnStr = 'None';
    if (vulnerable === 'All') vulnStr = 'All';
    else if (vulnerable === 'NS') vulnStr = 'NS';
    else if (vulnerable === 'EW') vulnStr = 'EW';
    else if (vulnerable === 'Both') vulnStr = 'All';
    
    // Convert deal object to PBN format
    const positions = ['N', 'E', 'S', 'W'];
    const dealStr = positions.map(pos => {
      const handData = deal[pos];
      return [handData.spades, handData.hearts, handData.diamonds, handData.clubs].join('.');
    }).join(' ');
    
    const pbn = `[Board "${boardNum}"]
[Dealer "${dealer}"]
[Vulnerable "${vulnStr}"]
[Deal "${dealer}:${dealStr}"]`;
    
    try {
      await navigator.clipboard.writeText(pbn);
      alert(`Board ${boardNum} PBN copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };
  
  const renderTravellerTable = (boardNum, compact = false) => {
    const results = travellersWithPercentages[boardNum];
    const isPairs = gameType === 'pairs';
    const isMobile = window.innerWidth <= 768;

    // Calculate scaled IMPs for IMP pairs (divide by number of other tables)
    const getScaledIMP = (impValue) => {
      if (!isIMPScoring || !impValue) return impValue;
      const numTables = results.length;
      if (numTables <= 1) return impValue;
      return (impValue / (numTables - 1)).toFixed(1);
    };

    // Helper function to extract last names from player names
    const getLastNames = (playersString, isNS) => {
      if (!playersString) return '';

      if (gameType === 'teams') {
        // For teams: split and get relevant partnership
        const players = playersString.split(', ');
        if (players.length === 4) {
          const relevantPlayers = isNS ? [players[0], players[1]] : [players[2], players[3]];
          const lastNames = relevantPlayers.map(name => {
            const parts = name.trim().split(' ');
            return parts[parts.length - 1];
          });
          return lastNames.join(' - ');
        }
      } else if (gameType === 'pairs') {
        // For pairs: the string already contains just the pair (2 players)
        // Split by ' - ' first (if already formatted), otherwise by ', '
        let players;
        if (playersString.includes(' - ')) {
          players = playersString.split(' - ');
        } else {
          players = playersString.split(', ');
        }

        const lastNames = players.map(name => {
          const parts = name.trim().split(' ');
          return parts[parts.length - 1];
        });
        return lastNames.join(' - ');
      }

      return playersString;
    };

    return (
      <table className={`traveller-table ${compact ? 'compact' : ''}`}>
        <thead>
          <tr>
            <th>NS</th>
            <th>EW</th>
            <th>Contract</th>
            <th>By</th>
            {isPairs ? (
              <>
                <th>NS+</th>
                <th>NS-</th>
                <th>{isIMPScoring ? 'NS IMPs+' : 'NS %'}</th>
                <th>{isIMPScoring ? 'EW IMPs+' : 'EW %'}</th>
              </>
            ) : (
              <>
                <th>NS+</th>
                <th>NS-</th>
                <th>NS IMP</th>
                <th>EW IMP</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {results.map((result, idx) => {
            // Format scores to show absolute values in correct column
            const formatScoreCols = (nsScore, ewScore) => {
              let nsPlus = '', nsMinus = '';

              if (nsScore) {
                const val = parseFloat(nsScore);
                if (val > 0) {
                  nsPlus = Math.abs(val).toString();
                } else if (val < 0) {
                  nsMinus = Math.abs(val).toString();
                }
              }

              // If NS+ is blank, check if there's an EW score to negate
              if (!nsPlus && !nsMinus && ewScore) {
                const val = parseFloat(ewScore);
                if (val > 0) {
                  nsMinus = Math.abs(val).toString();
                } else if (val < 0) {
                  nsPlus = Math.abs(val).toString();
                }
              }

              return { nsPlus, nsMinus };
            };

            const { nsPlus, nsMinus } = formatScoreCols(result.nsScore, result.ewScore);

            // Determine what to display in NS and EW columns
            let nsDisplay, ewDisplay;
            if (!isMobile) {
              // Desktop view: show last names for both teams and pairs
              nsDisplay = getLastNames(result.nsPlayers, true) || result.ns;
              ewDisplay = getLastNames(result.ewPlayers, false) || result.ew;
            } else {
              // Mobile: show team/pair numbers
              nsDisplay = result.ns;
              ewDisplay = result.ew;
            }

            return (
              <tr key={idx}>
                <td
                  className={gameType === 'pairs' && !isMobile ? 'clickable-pair-name' : ''}
                  onClick={() => {
                    if (gameType === 'pairs' && !isMobile) {
                      setSwitchToIndividual(result.ns);
                    }
                  }}
                >
                  {nsDisplay}
                </td>
                <td
                  className={gameType === 'pairs' && !isMobile ? 'clickable-pair-name' : ''}
                  onClick={() => {
                    if (gameType === 'pairs' && !isMobile) {
                      setSwitchToIndividual(result.ew);
                    }
                  }}
                >
                  {ewDisplay}
                </td>
                <td>{formatContract(result.contract, result.made, result.declarer)}</td>
                <td>{result.declarer}</td>
                {isPairs ? (
                  <>
                    <td>{nsPlus}</td>
                    <td>{nsMinus}</td>
                    <td>
                      {isIMPScoring
                        ? (result.nsPts > 0 ? getScaledIMP(result.nsPts) : '')
                        : `${result.nsPercentage?.toFixed(1) || '0.0'}%`
                      }
                    </td>
                    <td>
                      {isIMPScoring
                        ? (result.ewPts > 0 ? getScaledIMP(result.ewPts) : '')
                        : `${result.ewPercentage?.toFixed(1) || '0.0'}%`
                      }
                    </td>
                  </>
                ) : (
                  <>
                    <td>{nsPlus}</td>
                    <td>{nsMinus}</td>
                    <td>{result.nsPts > 0 ? result.nsPts : ''}</td>
                    <td>{result.ewPts > 0 ? result.ewPts : ''}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };
  
  return (
    <div className="travellers">
      <div className="traveller-controls">
        <div className="view-mode-toggle">
          <button 
            className={viewMode === 'all' ? 'active' : ''}
            onClick={() => setViewMode('all')}
          >
            All Boards
          </button>
          <button 
            className={viewMode === 'single' ? 'active' : ''}
            onClick={() => setViewMode('single')}
          >
            Single Board
          </button>
        </div>

        {viewMode === 'single' && (
          <div className="board-selector">
            <label>Board: </label>
            <select value={selectedBoard} onChange={(e) => setSelectedBoard(e.target.value)}>
              {boards.map(board => (
                <option key={board} value={board}>Board {board}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {viewMode === 'single' ? (
        <>
          {selectedBoard && handRecords && handRecords[selectedBoard] && (
            <>
              <div className="hand-display">
                <HandDiagram 
                  hand={handRecords[selectedBoard]} 
                  boardNumber={selectedBoard}
                  showCopyPBN={true}
                  onCopyPBN={() => copyPBN(selectedBoard, handRecords[selectedBoard])}
                  showDDA={true}
                />
              </div>
            </>
          )}

          {selectedBoard && travellersWithPercentages[selectedBoard] && 
            <>
              {renderTravellerTable(selectedBoard, false)}
            </>
          }
        </>
      ) : (
        <div className="all-boards-view">
          {boards.map(board => (
            <div key={board} className="board-section">
              {handRecords && handRecords[board] && (
                <div className="compact-hand-display">
                  <HandDiagram 
                    hand={handRecords[board]} 
                    boardNumber={board}
                    showCopyPBN={true}
                    onCopyPBN={() => copyPBN(board, handRecords[board])}
                    showDDA={true}
                  />
                </div>
              )}
              
              {renderTravellerTable(board, true)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeamScoresheet({ scores, travellers, handRecords, isMobile }) {
  const [selectedTeam, setSelectedTeam] = useState('');
  const [previewBoard, setPreviewBoard] = useState(null);
  const [mobilePreviewBoard, setMobilePreviewBoard] = useState(null);
  
  // Listen for team selection from scoresheet
  useEffect(() => {
    const handleSelectTeam = (event) => {
      setSelectedTeam(event.detail);
    };
    window.addEventListener('selectTeam', handleSelectTeam);
    return () => window.removeEventListener('selectTeam', handleSelectTeam);
  }, []);
  
  // Extract teams from scores
  const teams = scores.map(score => ({
    team: score.team,
    players: score.players
  }));
  
  // Get team scoresheet data
  const getTeamScoresheet = () => {
    if (!selectedTeam) return [];
    
    const teamData = [];
    const boards = Object.keys(travellers).sort((a, b) => parseInt(a) - parseInt(b));
    
    boards.forEach(boardNum => {
      const boardResults = travellers[boardNum];
      
      // Find NS and EW results for this team
      // Team X NS plays at row index: X-1 for team 1, X for team 2, etc
      // Team X EW plays at opposite row
      const teamNum = parseInt(selectedTeam);
      const nsRow = boardResults[teamNum - 1]; // Team 1 NS at row 0 (Table 1)
      const ewRow = boardResults[teamNum === 1 ? 1 : 0]; // Team 1 EW at row 1 (Table 2)
      
      if (nsRow && ewRow) {
        // Get IMPs from NS perspective.
        // nsPts carries the sign directly in EBU/USEBIO (positive gain, negative loss).
        // In BBO format nsPts is always ≥0 and losses are encoded as ewPts > 0.
        // Avoid subtracting ewPts directly — when formats store ewPts as a negative
        // (mirror of nsPts), doing nsPts − ewPts doubles the swing (12 − −12 = 24).
        const imps = nsRow.nsPts ? nsRow.nsPts : -(nsRow.ewPts > 0 ? nsRow.ewPts : 0);
        
        // Format scores - put absolute value in + or - column based on sign
        const formatScore = (score) => {
          if (!score) return { plus: '', minus: '' };
          const val = parseFloat(score);
          if (val > 0) {
            return { plus: Math.abs(val).toString(), minus: '' };
          } else if (val < 0) {
            return { plus: '', minus: Math.abs(val).toString() };
          }
          return { plus: '', minus: '' };
        };
        
        const nsScoreFormatted = formatScore(nsRow.nsScore);
        const ewScoreFormatted = formatScore(ewRow.ewScore);
        
        teamData.push({
          board: boardNum,
          ns: {
            contract: formatContract(nsRow.contract, nsRow.made, nsRow.declarer),
            declarer: nsRow.declarer,
            result: nsRow.made,
            nsPlus: nsScoreFormatted.plus,
            nsMinus: nsScoreFormatted.minus
          },
          ew: {
            contract: formatContract(ewRow.contract, ewRow.made, ewRow.declarer),
            declarer: ewRow.declarer,
            result: ewRow.made,
            ewPlus: ewScoreFormatted.plus,
            ewMinus: ewScoreFormatted.minus
          },
          imps: imps
        });
      }
    });
    
    return teamData;
  };
  
  const teamScoresheet = getTeamScoresheet();
  const selectedTeamData = teams.find(t => t.team === selectedTeam);
  
  // Set first board as default preview when team changes (DESKTOP ONLY)
  useEffect(() => {
    if (!isMobile && teamScoresheet.length > 0 && !previewBoard) {
      setPreviewBoard(teamScoresheet[0].board);
    }
  }, [teamScoresheet, previewBoard, isMobile]);
  
  // Copy PBN function
  const copyPBN = async (boardNum, hand) => {
    const { deal, dealer, vulnerable } = hand;
    
    let vulnStr = 'None';
    if (vulnerable === 'All') vulnStr = 'All';
    else if (vulnerable === 'NS') vulnStr = 'NS';
    else if (vulnerable === 'EW') vulnStr = 'EW';
    else if (vulnerable === 'Both') vulnStr = 'All';
    
    const positions = ['N', 'E', 'S', 'W'];
    const dealStr = positions.map(pos => {
      const handData = deal[pos];
      return [handData.spades, handData.hearts, handData.diamonds, handData.clubs].join('.');
    }).join(' ');
    
    const pbn = `[Board "${boardNum}"]
[Dealer "${dealer}"]
[Vulnerable "${vulnStr}"]
[Deal "${dealer}:${dealStr}"]`;
    
    try {
      await navigator.clipboard.writeText(pbn);
      alert(`Board ${boardNum} PBN copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };
  
  const renderTravellerForPreview = (boardNum) => {
    const results = travellers[boardNum];
    if (!results) return null;

    // Format player names for teams - extract last names for desktop view
    const formatPlayerNames = (players, isNS) => {
      if (!players) return '';
      const playerArray = players.split(', ');
      if (playerArray.length === 4) {
        if (!isMobile) {
          // Desktop: show last names only
          const relevantPlayers = isNS ? [playerArray[0], playerArray[1]] : [playerArray[2], playerArray[3]];
          const lastNames = relevantPlayers.map(name => {
            const parts = name.trim().split(' ');
            return parts[parts.length - 1]; // Get last part of name
          });
          return lastNames.join(' - ');
        } else {
          // Mobile: show full names
          if (isNS) {
            return `${playerArray[0]} - ${playerArray[1]}`;
          } else {
            return `${playerArray[2]} - ${playerArray[3]}`;
          }
        }
      }
      return players;
    };
    
    return (
      <div className="preview-traveller">
        <h4>Results</h4>
        <table>
          <thead>
            <tr>
              <th>NS</th>
              <th>EW</th>
              <th>Contract</th>
              <th>By</th>
              <th>NS+</th>
              <th>NS-</th>
              <th>NS IMP</th>
              <th>EW IMP</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result, idx) => {
              // Format scores
              const formatScoreCols = (nsScore, ewScore) => {
                let nsPlus = '', nsMinus = '';
                
                if (nsScore) {
                  const val = parseFloat(nsScore);
                  if (val > 0) {
                    nsPlus = Math.abs(val).toString();
                  } else if (val < 0) {
                    nsMinus = Math.abs(val).toString();
                  }
                }
                
                if (!nsPlus && !nsMinus && ewScore) {
                  const val = parseFloat(ewScore);
                  if (val > 0) {
                    nsMinus = Math.abs(val).toString();
                  } else if (val < 0) {
                    nsPlus = Math.abs(val).toString();
                  }
                }
                
                return { nsPlus, nsMinus };
              };
              
              const { nsPlus, nsMinus } = formatScoreCols(result.nsScore, result.ewScore);
              
              return (
                <tr key={idx}>
                  <td>{formatPlayerNames(result.nsPlayers, true) || result.ns}</td>
                  <td>{formatPlayerNames(result.ewPlayers, false) || result.ew}</td>
                  <td>{formatContract(result.contract, result.made, result.declarer)}</td>
                  <td>{result.declarer}</td>
                  <td>{nsPlus}</td>
                  <td>{nsMinus}</td>
                  <td>{result.nsPts > 0 ? result.nsPts : ''}</td>
                  <td>{result.nsPts < 0 ? Math.abs(result.nsPts) : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };
  
  // Get NS and EW pair names
  let nsPairName = '';
  let ewPairName = '';
  if (selectedTeamData) {
    const players = selectedTeamData.players;
    nsPairName = `${players[0]} - ${players[1]}`;
    ewPairName = `${players[2]} - ${players[3]}`;
  }
  
  return (
    <div className="team-scoresheet">
      <div className="team-selector">
        <label>Select Team: </label>
        <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
          <option value="">-- Choose a team --</option>
          {teams.map(team => (
            <option key={team.team} value={team.team}>
              Team {team.team}: {team.players.join(', ')}
            </option>
          ))}
        </select>
      </div>
      
      {selectedTeam && teamScoresheet.length > 0 && (
        <div className="team-scoresheet-layout">
          <div className="team-scoresheet-table">
            <h3>Team {selectedTeam} Scoresheet</h3>
          <table>
            <thead>
              <tr>
                <th className="empty-header"></th>
                <th colSpan="5" className="ns-section-header">{nsPairName}</th>
                <th colSpan="5" className="ew-section-header">{ewPairName}</th>
                <th colSpan="2" className="imps-header">IMPs</th>
                {isMobile && <th className="preview-header"></th>}
              </tr>
              <tr>
                <th className="board-header">{isMobile ? 'Bd' : 'Board'}</th>
                <th className="ns-section">Bid</th>
                <th className="ns-section">By</th>
                <th className="ns-section">{isMobile ? 'Tks' : 'Result'}</th>
                <th className="ns-section">NS+</th>
                <th className="ns-section">NS-</th>
                <th className="ew-section">Bid</th>
                <th className="ew-section">By</th>
                <th className="ew-section">{isMobile ? 'Tks' : 'Result'}</th>
                <th className="ew-section">EW+</th>
                <th className="ew-section">EW-</th>
                <th className="imps-sub">+</th>
                <th className="imps-sub">-</th>
                {isMobile && <th className="preview-header">View</th>}
              </tr>
            </thead>
            <tbody>
              {teamScoresheet.map((board, idx) => (
                <tr 
                  key={idx}
                  onClick={isMobile ? undefined : () => setPreviewBoard(board.board)}
                  className={previewBoard === board.board ? 'selected-row' : (isMobile ? '' : 'clickable-row')}
                >
                  <td className="board-cell">{board.board}</td>
                  <td className="ns-section">{board.ns.contract}</td>
                  <td className="ns-section">{board.ns.declarer}</td>
                  <td className="ns-section">{board.ns.result}</td>
                  <td className="ns-section">{board.ns.nsPlus}</td>
                  <td className="ns-section">{board.ns.nsMinus}</td>
                  <td className="ew-section">{board.ew.contract}</td>
                  <td className="ew-section">{board.ew.declarer}</td>
                  <td className="ew-section">{board.ew.result}</td>
                  <td className="ew-section">{board.ew.ewPlus}</td>
                  <td className="ew-section">{board.ew.ewMinus}</td>
                  <td className="imps-plus">{board.imps > 0 ? board.imps : ''}</td>
                  <td className="imps-minus">{board.imps < 0 ? Math.abs(board.imps) : ''}</td>
                  {isMobile && (
                    <td className="preview-button-cell">
                      <button 
                        className="mobile-preview-btn"
                        onClick={() => setMobilePreviewBoard(board.board)}
                      >
                        👁
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={isMobile ? "9" : "9"} className="empty-total"></td>
                <td colSpan="2" className="total-label">Overall IMPs</td>
                <td className="imps-plus total-cell">
                  {teamScoresheet.reduce((sum, b) => sum + (b.imps > 0 ? b.imps : 0), 0)}
                </td>
                <td className="imps-minus total-cell">
                  {Math.abs(teamScoresheet.reduce((sum, b) => sum + (b.imps < 0 ? b.imps : 0), 0))}
                </td>
                {isMobile && <td className="empty-view-cell"></td>}
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Traveller preview on the right - DESKTOP ONLY */}
        {!isMobile && (
          <div className="traveller-preview-panel">
            {previewBoard ? (
              <>
                {handRecords?.[previewBoard] && (
                  <HandDiagram 
                    hand={handRecords[previewBoard]} 
                    boardNumber={previewBoard}
                    showCopyPBN={true}
                    onCopyPBN={() => copyPBN(previewBoard, handRecords[previewBoard])}
                    showDDA={true}
                  />
                )}
                {renderTravellerForPreview(previewBoard)}
              </>
            ) : (
              <div className="no-board-selected">
                <p>Click a board to view details</p>
              </div>
            )}
          </div>
        )}
        
        {/* Mobile popup preview */}
        {isMobile && mobilePreviewBoard && (
          <div className="mobile-preview-popup" onClick={() => setMobilePreviewBoard(null)}>
            <div className="mobile-preview-content" onClick={(e) => e.stopPropagation()}>
              <button className="mobile-preview-close" onClick={() => setMobilePreviewBoard(null)}>×</button>
              {handRecords?.[mobilePreviewBoard] && (
                <HandDiagram 
                  hand={handRecords[mobilePreviewBoard]} 
                  boardNumber={mobilePreviewBoard}
                  showCopyPBN={true}
                  onCopyPBN={() => copyPBN(mobilePreviewBoard, handRecords[mobilePreviewBoard])}
                  showDDA={true}
                />
              )}
              {renderTravellerForPreview(mobilePreviewBoard)}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function IndividualScorecard({ travellers, gameType, handRecords, metadata, onPlayerClick, isMobile }) {
  const [selectedPair, setSelectedPair] = useState('');
  const [previewBoard, setPreviewBoard] = useState(null);
  const [mobilePreviewBoard, setMobilePreviewBoard] = useState(null);
  
  // Detect IMP scoring for pairs
  const isIMPScoring = gameType === 'pairs' && metadata?.Scoring === '2';
  
  // Calculate percentages for pairs (only for MP scoring)
  const travellersWithPercentages = (gameType === 'pairs' && !isIMPScoring)
    ? calculateTravellerPercentages(travellers) 
    : travellers;
  
  // Listen for pair selection from scoresheet
  useEffect(() => {
    const handleSelectPair = (event) => {
      setSelectedPair(event.detail);
    };
    window.addEventListener('selectPair', handleSelectPair);
    return () => window.removeEventListener('selectPair', handleSelectPair);
  }, []);
  
  // Extract all unique pairs/partnerships from travellers with their names
  const pairsMap = new Map();
  
  if (gameType === 'teams') {
    // For teams, extract partnerships from traveller data
    // Team 1 NS, Team 1 EW, Team 2 NS, Team 2 EW
    Object.values(travellersWithPercentages).forEach(results => {
      results.forEach((result) => {
        const teamNum = result.ns;

        if (teamNum && result.nsPlayers) {
          // Parse player names: "PlayerA, PlayerB, PlayerC, PlayerD"
          const players = result.nsPlayers.split(', ');
          if (players.length === 4) {
            const nsKey = `${teamNum}-NS`;
            const ewKey = `${teamNum}-EW`;
            
            if (!pairsMap.has(nsKey)) {
              pairsMap.set(nsKey, `Team ${teamNum} NS: ${players[0]} - ${players[1]}`);
            }
            if (!pairsMap.has(ewKey)) {
              pairsMap.set(ewKey, `Team ${teamNum} EW: ${players[2]} - ${players[3]}`);
            }
          }
        }
        
        // Also check EW team
        const ewTeamNum = result.ew;
        if (ewTeamNum && result.ewPlayers) {
          const players = result.ewPlayers.split(', ');
          if (players.length === 4) {
            const nsKey = `${ewTeamNum}-NS`;
            const ewKey = `${ewTeamNum}-EW`;
            
            if (!pairsMap.has(nsKey)) {
              pairsMap.set(nsKey, `Team ${ewTeamNum} NS: ${players[0]} - ${players[1]}`);
            }
            if (!pairsMap.has(ewKey)) {
              pairsMap.set(ewKey, `Team ${ewTeamNum} EW: ${players[2]} - ${players[3]}`);
            }
          }
        }
      });
    });
  } else {
    // For pairs, extract as before
    Object.values(travellersWithPercentages).forEach(results => {
      results.forEach(result => {
        if (result.ns && !pairsMap.has(result.ns)) {
          pairsMap.set(result.ns, result.nsPlayers || `Pair ${result.ns}`);
        }
        if (result.ew && !pairsMap.has(result.ew)) {
          pairsMap.set(result.ew, result.ewPlayers || `Pair ${result.ew}`);
        }
      });
    });
  }
  
  // Sort pairs by player names alphabetically
  const sortedPairs = Array.from(pairsMap.entries()).sort((a, b) => 
    a[1].localeCompare(b[1])
  );
  
  // Get percentage color - pastel colors
  const getPercentageColor = (percentage) => {
    if (percentage === null || percentage === undefined) return '';
    
    const pct = parseFloat(percentage);
    if (pct >= 50) {
      // Pastel Yellow (50%) to Pastel Green (100%)
      const ratio = (pct - 50) / 50;
      // Yellow: rgb(255, 253, 208), Green: rgb(198, 246, 213)
      const r = Math.round(255 - (57 * ratio));
      const g = Math.round(253 - (7 * ratio));
      const b = Math.round(208 + (5 * ratio));
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Pastel Pink (0%) to Pastel Yellow (50%)
      const ratio = pct / 50;
      // Pink: rgb(255, 209, 220), Yellow: rgb(255, 253, 208)
      const r = 255;
      const g = Math.round(209 + (44 * ratio));
      const b = Math.round(220 - (12 * ratio));
      return `rgb(${r}, ${g}, ${b})`;
    }
  };
  
  // Color coding for IMP scores with gradient
  const getIMPColor = (imps) => {
    const impValue = parseFloat(imps);
    if (isNaN(impValue)) return '';
    
    if (impValue >= 12) {
      // +12 and above: Pastel green
      return 'rgb(198, 246, 213)';
    } else if (impValue <= -12) {
      // -12 and below: Pastel pink
      return 'rgb(255, 209, 220)';
    } else if (impValue === 0) {
      // Zero: Pastel yellow
      return 'rgb(255, 253, 208)';
    } else if (impValue > 0) {
      // 0 to +12: Gradient from pastel yellow to pastel green
      const ratio = impValue / 12;
      // Yellow: rgb(255, 253, 208), Green: rgb(198, 246, 213)
      const r = Math.round(255 - (57 * ratio));
      const g = Math.round(253 - (7 * ratio));
      const b = Math.round(208 + (5 * ratio));
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // -12 to 0: Gradient from pastel pink to pastel yellow
      const ratio = (impValue + 12) / 12;
      // Pink: rgb(255, 209, 220), Yellow: rgb(255, 253, 208)
      const r = 255;
      const g = Math.round(209 + (44 * ratio));
      const b = Math.round(220 - (12 * ratio));
      return `rgb(${r}, ${g}, ${b})`;
    }
  };
  
  // Get results for selected pair
  const getPairResults = () => {
    if (!selectedPair) return [];
    
    const results = [];
    const boards = Object.keys(travellersWithPercentages).sort((a, b) => parseInt(a) - parseInt(b));
    
    if (gameType === 'teams') {
      // Parse partnership ID: "1-NS" or "1-EW"
      const [teamNum, direction] = selectedPair.split('-');
      
      boards.forEach(boardNum => {
        const boardResults = travellersWithPercentages[boardNum];
        
        // Determine which table/row to look at
        // Team X NS plays at: first row if X=1, second row if X=2
        // Team X EW plays at: second row if X=1, first row if X=2
        let tableIndex;
        let isNS;
        
        if (direction === 'NS') {
          // NS partnership
          tableIndex = parseInt(teamNum) === 1 ? 0 : 1; // Team 1 NS at Table 1 (row 0), Team 2 NS at Table 2 (row 1)
          isNS = true;
        } else {
          // EW partnership
          tableIndex = parseInt(teamNum) === 1 ? 1 : 0; // Team 1 EW at Table 2 (row 1), Team 2 EW at Table 1 (row 0)
          isNS = false;
        }
        
        const pairResult = boardResults[tableIndex];
        
        if (pairResult) {
          // Determine score
          let score = '';
          if (isNS) {
            score = pairResult.nsScore || (pairResult.ewScore ? `-${pairResult.ewScore}` : '');
          } else {
            score = pairResult.ewScore || (pairResult.nsScore ? `-${pairResult.nsScore}` : '');
          }
          
          // Get opponent team and partnership
          const opponentTeam = isNS ? pairResult.ew : pairResult.ns;
          const opponentDirection = isNS ? 'EW' : 'NS';
          const opponentPlayers = isNS ? pairResult.ewPlayers : pairResult.nsPlayers;
          
          // Parse opponent partnership names
          let opponentNames = `Team ${opponentTeam} ${opponentDirection}`;
          if (opponentPlayers) {
            const players = opponentPlayers.split(', ');
            if (players.length === 4) {
              if (opponentDirection === 'NS') {
                opponentNames = `${players[0]} - ${players[1]}`;
              } else {
                opponentNames = `${players[2]} - ${players[3]}`;
              }
            }
          }
          
          results.push({
            board: boardNum,
            direction: direction,
            opponents: `${opponentTeam}-${opponentDirection}`,
            opponentNames: opponentNames,
            contract: formatContract(pairResult.contract, pairResult.made, pairResult.declarer),
            declarer: pairResult.declarer,
            score: score,
            percentage: null,
            points: isNS ? pairResult.nsPts : pairResult.ewPts,
            hand: handRecords?.[boardNum]
          });
        }
      });
    } else {
      // Pairs logic (unchanged)
      boards.forEach(boardNum => {
        const boardResults = travellersWithPercentages[boardNum];
        const pairResult = boardResults.find(r => r.ns === selectedPair || r.ew === selectedPair);
        
        if (pairResult) {
          const isNS = pairResult.ns === selectedPair;
          
          // Determine score based on complex logic
          let score = '';
          if (isNS) {
            // Pair is sitting NS
            if (pairResult.nsScore) {
              score = pairResult.nsScore;
            } else if (pairResult.ewScore) {
              // NS+ is blank, use negative of NS-
              const ewScore = pairResult.ewScore;
              score = ewScore.startsWith('-') ? ewScore.substring(1) : `-${ewScore}`;
            }
          } else {
            // Pair is sitting EW
            if (pairResult.nsScore) {
              // Use negative of NS+
              const nsScore = pairResult.nsScore;
              score = nsScore.startsWith('-') ? nsScore.substring(1) : `-${nsScore}`;
            } else if (pairResult.ewScore) {
              // NS+ is blank, use NS-
              score = pairResult.ewScore;
            }
          }
          
          // Get percentage
          const percentage = isNS ? pairResult.nsPercentage : pairResult.ewPercentage;
          
          // Get raw IMP value
          let impValue = isNS ? pairResult.nsPts : pairResult.ewPts;
          
          // Scale IMPs for IMP pairs (divide by number of other tables)
          if (isIMPScoring && impValue) {
            const numTables = boardResults.length;
            if (numTables > 1) {
              impValue = parseFloat((impValue / (numTables - 1)).toFixed(1));
            }
          }
          
          results.push({
            board: boardNum,
            direction: isNS ? 'NS' : 'EW',
            opponents: isNS ? pairResult.ew : pairResult.ns,
            opponentNames: isNS ? (pairResult.ewPlayers || `Pair ${pairResult.ew}`) : (pairResult.nsPlayers || `Pair ${pairResult.ns}`),
            contract: formatContract(pairResult.contract, pairResult.made, pairResult.declarer),
            declarer: pairResult.declarer,
            score: score,
            percentage: percentage,
            points: isIMPScoring 
              ? impValue  // Scaled IMPs for IMP pairs
              : (gameType === 'pairs' ? percentage : (isNS ? pairResult.nsPts : pairResult.ewPts)),
            hand: handRecords?.[boardNum]
          });
        }
      });
    }
    
    return results;
  };
  
  const pairResults = getPairResults();
  const selectedPairName = pairsMap.get(selectedPair);
  
  // Set first board as default preview when results change (DESKTOP ONLY)
  useEffect(() => {
    if (!isMobile && pairResults.length > 0 && !previewBoard) {
      setPreviewBoard(pairResults[0].board);
    }
  }, [pairResults, previewBoard, isMobile]);
  
  // Copy PBN function
  const copyPBN = async (boardNum, hand) => {
    const { deal, dealer, vulnerable } = hand;
    
    let vulnStr = 'None';
    if (vulnerable === 'All') vulnStr = 'All';
    else if (vulnerable === 'NS') vulnStr = 'NS';
    else if (vulnerable === 'EW') vulnStr = 'EW';
    else if (vulnerable === 'Both') vulnStr = 'All';
    
    const positions = ['N', 'E', 'S', 'W'];
    const dealStr = positions.map(pos => {
      const handData = deal[pos];
      return [handData.spades, handData.hearts, handData.diamonds, handData.clubs].join('.');
    }).join(' ');
    
    const pbn = `[Board "${boardNum}"]
[Dealer "${dealer}"]
[Vulnerable "${vulnStr}"]
[Deal "${dealer}:${dealStr}"]`;
    
    try {
      await navigator.clipboard.writeText(pbn);
      alert(`Board ${boardNum} PBN copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };
  
  const renderTravellerForPreview = (boardNum) => {
    const results = travellersWithPercentages[boardNum];
    if (!results) return null;
    
    // Calculate scaled IMPs for IMP pairs (divide by number of other tables)
    const getScaledIMP = (impValue) => {
      if (!isIMPScoring || !impValue) return impValue;
      const numTables = results.length;
      if (numTables <= 1) return impValue;
      return (impValue / (numTables - 1)).toFixed(1);
    };
    
    // Helper to format player names - extract last names for desktop teams view
    const formatPlayerNames = (players, isNS) => {
      if (!players) return '';

      if (gameType === 'teams' && !isMobile) {
        // For teams on desktop, parse and show only last names of relevant partnership
        const playerArray = players.split(', ');
        if (playerArray.length === 4) {
          const relevantPlayers = isNS ? [playerArray[0], playerArray[1]] : [playerArray[2], playerArray[3]];
          const lastNames = relevantPlayers.map(name => {
            const parts = name.trim().split(' ');
            return parts[parts.length - 1]; // Get last part of name
          });
          return lastNames.join(' - ');
        }
      }

      return players;
    };
    
    return (
      <div className="preview-traveller">
        <h4>Results</h4>
        <table>
          <thead>
            <tr>
              <th>NS</th>
              <th>EW</th>
              <th>Contract</th>
              <th>By</th>
              <th>NS+</th>
              <th>NS-</th>
              {gameType === 'pairs' ? (
                <>
                  <th>{isIMPScoring ? 'NS IMPs+' : 'NS %'}</th>
                  <th>{isIMPScoring ? 'EW IMPs+' : 'EW %'}</th>
                </>
              ) : (
                <>
                  <th>NS IMP</th>
                  <th>EW IMP</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {results.map((result, idx) => {
              // Format scores
              const formatScoreCols = (nsScore, ewScore) => {
                let nsPlus = '', nsMinus = '';
                
                if (nsScore) {
                  const val = parseFloat(nsScore);
                  if (val > 0) {
                    nsPlus = Math.abs(val).toString();
                  } else if (val < 0) {
                    nsMinus = Math.abs(val).toString();
                  }
                }
                
                if (!nsPlus && !nsMinus && ewScore) {
                  const val = parseFloat(ewScore);
                  if (val > 0) {
                    nsMinus = Math.abs(val).toString();
                  } else if (val < 0) {
                    nsPlus = Math.abs(val).toString();
                  }
                }
                
                return { nsPlus, nsMinus };
              };
              
              const { nsPlus, nsMinus } = formatScoreCols(result.nsScore, result.ewScore);
              
              return (
                <tr key={idx}>
                  <td>
                    {isMobile && gameType === 'pairs' 
                      ? result.ns 
                      : (formatPlayerNames(result.nsPlayers, true) || result.ns)
                    }
                  </td>
                  <td>
                    {isMobile && gameType === 'pairs'
                      ? result.ew
                      : (formatPlayerNames(result.ewPlayers, false) || result.ew)
                    }
                  </td>
                  <td>{formatContract(result.contract, result.made, result.declarer)}</td>
                  <td>{result.declarer}</td>
                  <td>{nsPlus}</td>
                  <td>{nsMinus}</td>
                  {gameType === 'pairs' ? (
                    <>
                      <td>
                        {isIMPScoring 
                          ? (result.nsPts > 0 ? getScaledIMP(result.nsPts) : '') 
                          : `${result.nsPercentage?.toFixed(1) || '0.0'}%`
                        }
                      </td>
                      <td>
                        {isIMPScoring 
                          ? (result.nsPts < 0 ? getScaledIMP(Math.abs(result.nsPts)) : '') 
                          : `${result.ewPercentage?.toFixed(1) || '0.0'}%`
                        }
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{result.nsPts > 0 ? result.nsPts : ''}</td>
                      <td>{result.nsPts < 0 ? Math.abs(result.nsPts) : ''}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };
  
  return (
    <div className="individual-scorecard">
      <div className="pair-selector">
        <label>Select Pair: </label>
        <select value={selectedPair} onChange={(e) => setSelectedPair(e.target.value)}>
          <option value="">-- Choose a pair --</option>
          {sortedPairs.map(([pairNum, pairName]) => (
            <option key={pairNum} value={pairNum}>{pairName}</option>
          ))}
        </select>
      </div>
      
      {selectedPair && pairResults.length > 0 && (
        <div className="scorecard-layout">
          <div className="scorecard-table-container">
            <h3>Results for {selectedPairName}</h3>
            <table className="individual-scorecard">
              <thead>
                <tr>
                  <th>{isMobile ? 'Bd' : 'Board'}</th>
                  <th>{isMobile ? 'Dir' : 'Direction'}</th>
                  <th>Opponents</th>
                  <th>Contract</th>
                  <th>By</th>
                  <th>{isMobile ? 'Scr' : 'Score'}</th>
                  <th>
                    {gameType === 'teams' ? 'IMPs' : (isIMPScoring ? 'IMPs' : '%')}
                  </th>
                  {isMobile && <th>View</th>}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Group consecutive boards with same opponents
                  const groups = [];
                  let currentGroup = null;
                  
                  pairResults.forEach((result, idx) => {
                    if (!currentGroup || 
                        currentGroup.opponents !== result.opponents ||
                        currentGroup.direction !== result.direction) {
                      // Start new group
                      if (currentGroup) groups.push(currentGroup);
                      currentGroup = {
                        opponents: result.opponents,
                        opponentNames: result.opponentNames,
                        direction: result.direction,
                        results: [result]
                      };
                    } else {
                      // Add to current group
                      currentGroup.results.push(result);
                    }
                  });
                  if (currentGroup) groups.push(currentGroup);
                  
                  // Render groups with rowspan
                  return groups.flatMap((group, groupIdx) => 
                    group.results.map((result, resultIdx) => (
                      <tr 
                        key={result.board}
                        onClick={isMobile ? undefined : () => setPreviewBoard(result.board)}
                        className={previewBoard === result.board ? 'selected-row' : (isMobile ? '' : 'clickable-row')}
                      >
                        <td>{result.board}</td>
                        {resultIdx === 0 && (
                          <>
                            <td rowSpan={group.results.length}>{group.direction}</td>
                            <td 
                              rowSpan={group.results.length}
                              className="clickable-opponents"
                              onClick={(e) => {
                                e.stopPropagation();
                                onPlayerClick && onPlayerClick(group.opponents);
                              }}
                            >
                              {group.opponentNames}
                            </td>
                          </>
                        )}
                        <td>{result.contract}</td>
                        <td>{result.declarer}</td>
                        <td>{result.score}</td>
                        <td style={{ 
                          backgroundColor: gameType === 'pairs' && !isIMPScoring ? getPercentageColor(result.percentage) : 
                                          isIMPScoring ? getIMPColor(result.points) : 'transparent',
                          color: '#000'
                        }}>
                          {gameType === 'teams' ? result.points : 
                           isIMPScoring ? result.points : 
                           `${result.percentage?.toFixed(1) || '0.0'}%`}
                        </td>
                        {isMobile && (
                          <td className="preview-button-cell">
                            <button 
                              className="mobile-preview-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMobilePreviewBoard(result.board);
                              }}
                            >
                              👁️
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  );
                })()}
              </tbody>
            </table>
            
            {gameType === 'pairs' && !isIMPScoring && (
              <div className="scorecard-summary">
                <strong>Average: {(pairResults.reduce((sum, r) => sum + (parseFloat(r.percentage) || 0), 0) / pairResults.length).toFixed(1)}%</strong>
              </div>
            )}
            
            {isIMPScoring && (
              <div className="scorecard-summary">
                <strong>Total IMPs: {pairResults.reduce((sum, r) => sum + (parseFloat(r.points) || 0), 0).toFixed(1)}</strong>
              </div>
            )}
          </div>
          
          {/* Traveller preview on the right - DESKTOP ONLY */}
          {!isMobile && (
            <div className="traveller-preview-panel">
              {previewBoard ? (
                <>
                  {handRecords?.[previewBoard] && (
                    <HandDiagram 
                      hand={handRecords[previewBoard]} 
                      boardNumber={previewBoard}
                      showCopyPBN={true}
                      onCopyPBN={() => copyPBN(previewBoard, handRecords[previewBoard])}
                      showDDA={true}
                    />
                  )}
                  {renderTravellerForPreview(previewBoard)}
                </>
              ) : (
                <div className="no-board-selected">
                  <p>Click a board to view details</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Mobile Preview Popup */}
      {isMobile && mobilePreviewBoard && (
        <div className="mobile-preview-popup" onClick={() => setMobilePreviewBoard(null)}>
          <div className="mobile-preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-preview-close" onClick={() => setMobilePreviewBoard(null)}>×</button>
            {handRecords?.[mobilePreviewBoard] && (
              <HandDiagram 
                hand={handRecords[mobilePreviewBoard]} 
                boardNumber={mobilePreviewBoard}
                showCopyPBN={true}
                onCopyPBN={() => copyPBN(mobilePreviewBoard, handRecords[mobilePreviewBoard])}
                showDDA={true}
              />
            )}
            {renderTravellerForPreview(mobilePreviewBoard)}
          </div>
        </div>
      )}
    </div>
  );
}

export default GameView;
